import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  type?: 'website' | 'article' | 'product';
}

export default function SEO({ title, description, type = 'website' }: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Schema.org JSON-LD para SoftwareApplication
    const softwareSchema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Agro Ponto',
      description: 'Sistema completo de controle de ponto para trabalhadores rurais com geolocalização, reconhecimento facial e funcionamento offline',
      url: siteUrl,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Android, iOS, Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'BRL'
      }
    };

    // Schema.org JSON-LD para Organization
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Agro Ponto',
      url: siteUrl,
      description: 'Empresa especializada em soluções de controle de ponto para o agronegócio',
      foundingDate: '2005',
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: '50+'
      }
    };

    // Schema.org JSON-LD para FAQPage
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Como funciona o controle de ponto rural?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Nosso sistema permite que trabalhadores rurais registrem ponto diretamente do campo usando dispositivos móveis. O sistema funciona offline e sincroniza automaticamente quando há conexão disponível.'
          }
        },
        {
          '@type': 'Question',
          name: 'Funciona sem internet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim! O sistema foi desenvolvido especialmente para o campo. Todos os registros são salvos localmente e sincronizados automaticamente quando houver conexão, garantindo que nenhum dado seja perdido.'
          }
        },
        {
          '@type': 'Question',
          name: 'Como garante que não há fraude?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Utilizamos geolocalização precisa, reconhecimento facial e validação de horários. Cada registro é verificado e armazenado de forma segura, impossibilitando alterações não autorizadas.'
          }
        },
        {
          '@type': 'Question',
          name: 'É difícil de usar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Não! O sistema foi projetado para ser extremamente simples. Com apenas alguns toques, o trabalhador registra entrada e saída. A interface é intuitiva e não requer treinamento complexo.'
          }
        },
        {
          '@type': 'Question',
          name: 'Quanto tempo leva para implementar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A implementação é rápida. Em poucos dias sua equipe já estará usando o sistema. Fornecemos todo o suporte necessário para garantir uma transição suave e sem complicações.'
          }
        },
        {
          '@type': 'Question',
          name: 'Qual o suporte oferecido?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Oferecemos suporte técnico completo via telefone, e-mail e chat. Nossa equipe está disponível para ajudar com qualquer dúvida ou problema, garantindo que seu sistema funcione perfeitamente.'
          }
        }
      ]
    };

    // Schema.org JSON-LD para WebSite
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Agro Ponto',
      url: siteUrl
    };

    // Remover schemas antigos se existirem
    const oldSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    oldSchemas.forEach(schema => schema.remove());

    // Adicionar novos schemas
    const schemas = [softwareSchema, organizationSchema, faqSchema, websiteSchema];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Atualizar canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = siteUrl + window.location.pathname;

    // Atualizar Open Graph URL
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = siteUrl + window.location.pathname;

    // Atualizar Twitter URL
    let twitterUrl = document.querySelector('meta[name="twitter:url"]') as HTMLMetaElement;
    if (!twitterUrl) {
      twitterUrl = document.createElement('meta');
      twitterUrl.setAttribute('name', 'twitter:url');
      document.head.appendChild(twitterUrl);
    }
    twitterUrl.content = siteUrl + window.location.pathname;

    // Cleanup ao desmontar
    return () => {
      const schemasToRemove = document.querySelectorAll('script[type="application/ld+json"]');
      schemasToRemove.forEach(schema => schema.remove());
    };
  }, [title, description, type]);

  return null;
}